// ObservabilityOperationsDetail.jsx — Left TOC + scroll-spy + RIGHT Sponsored
// Updated to match your “Template / Example badge on the right” style (like InterviewGamePlanDetail.jsx, etc.)
// More readable + more detailed, and uses ONE consistent running example: “Facebook-like Home Feed”.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ================== AdSense helpers ================== */
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      // Route navigation: try pushing again (safe to ignore errors)
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {}
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <h3 className="h6 text-uppercase text-muted mb-2">Sponsored</h3>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ================== small UI helpers ================== */
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-5">
    <h2 className="h4 mb-2">{title}</h2>
    {children}
    <div className="mt-3">
      <a href="#top" className="small text-muted">
        ↑ Back to top
      </a>
    </div>
  </section>
);

function BadgeRight({ children, tone = "secondary" }) {
  return (
    <span className={`badge bg-${tone} text-white`} style={{ whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function LabeledCard({ label, tone, title, children }) {
  return (
    <div className="card border-0 shadow-sm mt-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
          <div className="fw-semibold">{title}</div>
          <BadgeRight tone={tone}>{label}</BadgeRight>
        </div>
        <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const TemplateCard = ({ title = "Template", children }) => (
  <LabeledCard label="Template" tone="secondary" title={title}>
    {children}
  </LabeledCard>
);

const ExampleCard = ({ title = "Facebook-like Home Feed — example", children }) => (
  <LabeledCard label="Example" tone="success" title={title}>
    {children}
  </LabeledCard>
);

function CodeCard({ title = "Template — snippets", children }) {
  return (
    <div className="card border-0 shadow-sm mt-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
          <div className="fw-semibold">{title}</div>
          <BadgeRight tone="secondary">Template</BadgeRight>
        </div>
        <pre
          className="bg-light rounded p-3 border small mb-0"
          style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
        >
          <code>{children}</code>
        </pre>
      </div>
    </div>
  );
}

function SayCallout({ children }) {
  return (
    <div className="alert alert-primary d-flex align-items-center mb-3" role="alert">
      <div className="me-2 fw-semibold">Say</div>
      <div className="mb-0">{children}</div>
    </div>
  );
}

const TOC = [
  { id: "golden-signals", label: "A) Golden signals (RED/USE)" },
  { id: "metrics-logs-traces", label: "B) Metrics, logs & traces" },
  { id: "slo-alerting", label: "C) SLOs, error budgets, alerts" },
  { id: "dashboards", label: "D) Dashboards & drill-down flows" },
  { id: "rollouts", label: "E) Rollouts & feature flags" },
  { id: "incident", label: "F) Incident response & postmortems" },
  { id: "capacity-autoscaling", label: "G) Capacity & autoscaling" },
  { id: "snippets", label: "Practical snippets" },
  { id: "one-liners", label: "Interview one-liners" },
];

/* ================== page ================== */
export default function ObservabilityOperationsDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  // scroll-spy
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActiveId(e.target.id);
      });
    }, opts);

    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  const feed = useMemo(
    () => ({
      name: "Facebook-like Home Feed",
      keyEndpoints: [
        "GET /feed",
        "GET /feed/next",
        "POST /like",
        "POST /comment",
        "POST /follow",
      ],
      deps: ["API Gateway", "Feed API", "Ranking service", "Graph service", "Redis", "DB", "Kafka"],
      slo: { availability: "99.9%", latencyP95: "≤ 200ms (GET /feed)" },
    }),
    []
  );

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Observability &amp; operations</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 text-center rounded-3 p-3 p-md-4"
        style={{ background: "linear-gradient(135deg, rgba(25,135,84,.07), rgba(13,110,253,.06))" }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-1">9) Observability &amp; operations — detailed guide</h1>
          <p className="text-muted mb-0">
            Golden signals, metrics/logs/traces, SLO-based alerting, dashboards, safe rollouts,
            incident response, and autoscaling — with a {feed.name} example in every section.
          </p>
        </div>
      </header>

      {/* layout: LEFT TOC + MAIN + RIGHT Sponsored */}
      <div className="row g-4">
        {/* LEFT TOC */}
        <aside className="col-12 col-xl-2 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h3 className="h6 text-uppercase text-muted mb-2">On this page</h3>
                <nav className="nav flex-column small">
                  {TOC.map(({ id, label }) => (
                    <a
                      key={id}
                      href={`#${id}`}
                      className={`nav-link px-0 ${activeId === id ? "fw-semibold text-primary" : ""}`}
                      aria-current={activeId === id ? "true" : undefined}
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            <div className="d-none d-xl-block small text-muted mt-2">
              Tip: Best dashboards are “one-click drill-down” (metrics → traces → logs).
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7">
          {/* A) Golden signals */}
          <Section id="golden-signals" title="A) Golden signals (RED/USE): latency, traffic, errors, saturation">
            <SayCallout>“I start with golden signals, then drill down with traces and logs.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Two useful lenses</h3>
            <ul className="mb-3">
              <li>
                <strong>RED (user-facing endpoints):</strong> Requests, Errors, Duration (latency).
              </li>
              <li>
                <strong>USE (infrastructure/dependencies):</strong> Utilization, Saturation, Errors.
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">What each signal means (and common pitfalls)</h3>
            <ul className="mb-0">
              <li>
                <strong>Latency:</strong> track p50/p95/p99 server-side and end-to-end. Avoid averages; retries inflate tails.
              </li>
              <li>
                <strong>Traffic:</strong> RPS/QPS, concurrency (in-flight), bytes in/out. Separate reads vs writes.
              </li>
              <li>
                <strong>Errors:</strong> 5xx, timeouts, dependency errors, and business failures (e.g., “no feed candidates”).
              </li>
              <li>
                <strong>Saturation:</strong> CPU, memory/GC, thread pools, DB pool, queue depth, disk IO. CPU alone lies.
              </li>
            </ul>

            <TemplateCard title="Golden signals — template (how to answer fast)">
{`• RED for endpoints: request rate, error rate, duration (p95/p99).
• USE for dependencies: utilization, saturation, errors (DB, cache, queues).
• Always include: tail latency + saturation (p95 can degrade before errors).
• Pitfall checks: retries, timeouts, cardinality explosions.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — golden signals we publish`}>
{`RED (endpoints):
• GET /feed: RPS, p95/p99, 5xx + timeout rate, “empty feed” rate
• POST /like/comment: RPS, p95, error rate

USE (dependencies):
• Redis: hit rate, latency, CPU, evictions
• DB: pool usage, slow queries, replica lag
• Kafka: producer errors, consumer lag, DLQ rate
• Ranking service: p95, error rate, breaker opens`}
            </ExampleCard>
          </Section>

          {/* In-article ad (optional) */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90 }} />
            </div>
          )}

          {/* B) Metrics, logs, traces */}
          <Section id="metrics-logs-traces" title="B) Metrics, logs & traces (sampling, exemplars, cardinality)">
            <SayCallout>“Metrics detect fast; traces explain paths; logs give detail.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Metrics (what to standardize)</h3>
            <ul className="mb-3">
              <li>
                Prefer <strong>histograms</strong> for latency, not averages.
              </li>
              <li>
                Standard dimensions: <code>service</code>, <code>route</code>, <code>status_class</code>, <code>region</code>,{" "}
                <code>env</code>, <code>dependency</code>.
              </li>
              <li>
                <strong>Cardinality guardrails:</strong> never label by userId/requestId; sample or aggregate those.
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Logs (what “good” looks like)</h3>
            <ul className="mb-3">
              <li>
                Structured JSON logs with: <code>trace_id</code>, <code>span_id</code>, <code>request_id</code>,{" "}
                <code>route</code>, <code>tenant</code>, outcome, latency, dependency errors.
              </li>
              <li>Redact PII at source; never log secrets/tokens; sample INFO if needed, but keep ERROR.</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Traces (and sampling strategy)</h3>
            <ul className="mb-0">
              <li>
                Propagate <strong>W3C TraceContext</strong> across HTTP + async boundaries (queues/streams).
              </li>
              <li>
                Use <strong>head-based sampling</strong> at ingress (cheap) + <strong>tail-based “keep slow/error”</strong> in collector.
              </li>
              <li>
                Add <strong>exemplars</strong> so a bad p95/p99 point links to a representative trace.
              </li>
            </ul>

            <TemplateCard title="Signals — template (what to say)">
{`• Metrics: RED/USE, histograms for latency, avoid high-card labels.
• Logs: structured JSON, trace/span IDs, PII redaction.
• Traces: W3C propagation across all boundaries; head + tail sampling.
• Exemplars: click from a metric spike to the exact trace.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — tracing & logging practice`}>
{`• Every request gets request_id + trace_id. Route is normalized ("/feed/:userId" becomes "/feed/{userId}").
• Tail sampling keeps: errors, timeouts, and top 1% slow traces.
• Async: Kafka messages carry traceparent; consumers create child spans.
• Logs redact PII; “user_id” appears only as a hashed surrogate when needed.`}
            </ExampleCard>
          </Section>

          {/* C) SLOs & alerting */}
          <Section id="slo-alerting" title="C) SLOs, error budgets, and alerts (page on burn, not noise)">
            <SayCallout>“I page on user impact: SLO burn rate, not every CPU spike.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Define SLIs/SLOs clearly</h3>
            <ul className="mb-3">
              <li>
                Availability SLI: <strong>good / total</strong> from the same population (avoid mixing sources).
              </li>
              <li>
                Latency SLI: e.g., “p95 &lt; 200ms” for GET /feed; define success (exclude cancels?) explicitly.
              </li>
              <li>
                Error budget = <strong>1 − SLO</strong> (99.9% → 0.1% budget).
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Burn-rate alerting (multi-window)</h3>
            <ul className="mb-0">
              <li>
                Fast page: detects sharp outages; Slow ticket: catches gradual regressions.
              </li>
              <li>
                Pair windows (e.g., 10m and 1h) to reduce flapping.
              </li>
            </ul>

            <CodeCard title="Burn rate — template math">
{`error_budget_fraction = 1 - SLO

burn_rate ≈ error_rate / error_budget_fraction

Typical approach:
• Page: 10m burn_rate high AND 1h burn_rate high
• Ticket: 6h burn_rate moderate` }
            </CodeCard>

            <TemplateCard title="SLO alerting — template (simple, interview-safe)">
{`• Define a small set of user-journey SLOs (not 200 micro-SLOs).
• Alert on error budget burn with multi-window rules (page vs ticket).
• Use guardrails: if burn is high, freeze risky releases and enable brownout flags.
• Keep on-call humane: reduce noise; focus on user impact.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — SLOs & paging rules`}>
{`SLOs:
• GET /feed availability: 99.9% monthly
• GET /feed latency: p95 ≤ 200ms
• “Freshness”: 99% of feed events processed within 5s

Alerts:
• Page when fast+slow burn indicate we’ll consume 1 day of budget quickly.
• Ticket when burn is moderate over 6–24h (slow regression).
• If budget burn is high: auto rollback canary + enable brownout (serve cached feed).`}
            </ExampleCard>
          </Section>

          {/* D) Dashboards */}
          <Section id="dashboards" title="D) Dashboards & drill-down flows (metrics → traces → logs)">
            <SayCallout>“A good dashboard answers: ‘Are users hurt? Where? Why?’ in under 2 minutes.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Dashboard structure (simple and effective)</h3>
            <ul className="mb-0">
              <li>
                <strong>Top:</strong> SLO status + error budget burn + p95/p99 + error rate.
              </li>
              <li>
                <strong>Middle:</strong> dependency panels (Redis/DB/Kafka) with saturation + latency.
              </li>
              <li>
                <strong>Bottom:</strong> “Drill-down” links: exemplar trace links, log queries, runbook links.
              </li>
            </ul>

            <TemplateCard title="Dashboards — template (what to build)">
{`• One “service overview” dashboard: SLO + golden signals + top dependencies.
• One “dependency deep dive” dashboard: DB/Redis/Kafka with saturation + latency.
• One-click drill-down: click from metric series to exemplar trace, then to logs.
• Add runbook links beside the panels (“what to do if this spikes”).`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — drill-down flow`}>
{`If GET /feed p99 spikes:
1) Overview shows burn rate + which region/route is impacted.
2) Dependency panel shows Redis latency up + hit rate down.
3) Click exemplar trace → see slow spans in “Redis GET candidates”.
4) Click logs for that trace_id → confirm timeouts and circuit breaker opens.
5) Runbook suggests: enable cached feed + scale Redis / reduce query fanout.`}
            </ExampleCard>
          </Section>

          {/* E) Rollouts */}
          <Section id="rollouts" title="E) Rollouts: canary, blue/green, feature flags (ship safely)">
            <SayCallout>“I ship with canaries + gates + instant rollback. Flags are the emergency brake.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Progressive delivery (canary)</h3>
            <ul className="mb-3">
              <li>Rollout steps: 1% → 5% → 25% → 100% with automated gates.</li>
              <li>Gate on: p95 latency delta, error delta, saturation delta, SLO burn delta.</li>
              <li>Shadow traffic for read-only diff testing (where safe).</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Feature flags (operational superpower)</h3>
            <ul className="mb-0">
              <li>Kill switch, % rollout, per-tenant allowlist; default-off for risky features.</li>
              <li>Audit changes; short TTL configs; don’t let flags live forever (cleanup checklist).</li>
            </ul>

            <TemplateCard title="Rollouts — template (how to describe)">
{`• Canary with automated gates: latency/error/saturation deltas + SLO burn.
• Blue/green when you want instant full rollback at the edge.
• Flags: kill switch + % rollout + tenant allowlist; audit trail; cleanup plan.
• Always have a safe fallback mode (cached/stale) for user-facing reads.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — safe rollout of new ranking model`}>
{`• Canary new model to 1% of users in one region.
• Gate: if p95 ↑ > 10% or error rate ↑ > 0.05% → auto rollback.
• Feature flag to disable “expensive embedding enrichment” instantly without redeploy.
• Shadow compare: old vs new ranking output for offline diffing.`}
            </ExampleCard>
          </Section>

          {/* F) Incident response */}
          <Section id="incident" title="F) Incident response, runbooks, postmortems (operate like a pro)">
            <SayCallout>“Stabilize first, then diagnose. One channel, clear roles, and frequent updates.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">During an incident</h3>
            <ul className="mb-3">
              <li>
                <strong>Roles:</strong> IC, Comms, Scribe, SMEs (DB/Cache/App).
              </li>
              <li>
                <strong>Flow:</strong> declare severity → stabilize (rollback/brownout/rate-limit) → diagnose → fix → verify.
              </li>
              <li>
                <strong>Hygiene:</strong> IC controls changes; update cadence 10–15 minutes.
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Runbooks (what they should contain)</h3>
            <ul className="mb-3">
              <li>Trigger thresholds, scope checks, decision tree, links/commands, rollback steps, owner/escalation.</li>
              <li>Verification after each action (“did p95 improve?”).</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Postmortems (blameless, actionable)</h3>
            <ul className="mb-0">
              <li>Timeline, impact, SLIs, contributing factors, what worked/failed, actions with owners and due dates.</li>
            </ul>

            <TemplateCard title="Incidents — template (interview-ready)">
{`• Declare severity + assign roles (IC/comms/scribe/SMEs).
• Stabilize: rollback, rate-limit, brownout/bailout, increase capacity.
• Diagnose with drill-down: metrics → traces → logs.
• Postmortem: blameless, actions tracked like features with owners + due dates.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — incident scenario`}>
{`Symptom: feed p99 spikes + 5xx increases.
• Stabilize: enable cached feed brownout; open breaker to ranking-service; rollback canary.
• Diagnose: Redis hit rate down + slow spans in “fetch candidates”.
• Fix: scale Redis, reduce key cardinality, adjust TTLs.
• PM: add guardrail alert on hit-rate collapse + add runbook step for fast brownout.`}
            </ExampleCard>
          </Section>

          {/* G) Capacity & autoscaling */}
          <Section id="capacity-autoscaling" title="G) Capacity planning & autoscaling (scale on the right signals)">
            <SayCallout>“I scale with leading indicators: RPS/pod, queue depth, lag — not CPU alone.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Capacity planning basics</h3>
            <ul className="mb-3">
              <li>
                Concurrency ≈ <strong>RPS × latency</strong> (Little’s Law intuition).
              </li>
              <li>
                Keep utilization at peak ~60–70% so you have headroom for spikes and failures.
              </li>
              <li>
                Model dependencies: DB pool size, cache capacity, partition counts, connection limits.
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Autoscaling strategies</h3>
            <ul className="mb-0">
              <li>
                <strong>APIs:</strong> HPA on CPU + custom metrics (RPS/pod, p95). Add stabilization windows.
              </li>
              <li>
                <strong>Consumers:</strong> scale on lag + processing time; cap in-flight per partition; pre-warm for known spikes.
              </li>
              <li>
                <strong>Stateful:</strong> scale reads via replicas; be careful with connection pools and cache warmup.
              </li>
              <li>
                <strong>Pitfalls:</strong> CPU-only scaling, no max caps, scaling the wrong tier.
              </li>
            </ul>

            <TemplateCard title="Autoscaling — template (what to say)">
{`• Plan with concurrency and headroom; validate against peak traffic.
• Autoscale APIs on RPS/pod + p95 (plus CPU), with stabilization to avoid thrash.
• Autoscale consumers on lag + processing time; cap in-flight per partition.
• Align limits: connection pools, queue depths, and upstream timeouts.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — scaling plan`}>
{`• Feed API scales on RPS/pod and p95 latency; cooldown prevents flapping.
• Ranking consumers scale on Kafka lag; cap in-flight to protect DB and Redis.
• DB read replicas scale for feed reads; connection pools are bounded and coordinated with pod count.`}
            </ExampleCard>
          </Section>

          {/* Practical snippets */}
          <Section id="snippets" title="Practical snippets (quote/adapt)">
            <CodeCard title="PromQL-ish — quick templates">
{`# Error rate (5xx)
sum(rate(http_requests_total{status=~"5.."}[5m]))
/
sum(rate(http_requests_total[5m]))

# Latency (p95) from histogram
histogram_quantile(
  0.95,
  sum(rate(http_request_duration_seconds_bucket[5m])) by (le, route)
)

# Saturation: DB pool usage ratio
max(db_pool_in_use) / max(db_pool_capacity)

# Kafka consumer lag (conceptual)
max(kafka_consumer_group_lag{group="feed-ranker"})` }
            </CodeCard>

            <TemplateCard title="Alert hygiene — template checklist">
{`• Every alert has: owner, severity, runbook link, and “what good looks like”.
• Page only on user impact (SLO burn). Everything else → ticket or dashboard.
• Avoid flapping: multi-window burn, for: durations, and stabilization windows.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — quick runbook links`}>
{`• “Feed p99 spike” runbook:
  - Check SLO burn dashboard
  - Check Redis hit rate + latency
  - Open exemplar trace (slow span)
  - Enable cached feed brownout if needed
  - Rollback latest canary if regression is suspected`}
            </ExampleCard>
          </Section>

          {/* One-liners */}
          <Section id="one-liners" title="Interview one-liners">
            <ul className="mb-0">
              <li>“Page on SLO burn, not raw metrics.”</li>
              <li>“Metrics detect, traces explain, logs provide detail.”</li>
              <li>“Exemplars link a bad p99 to the exact trace.”</li>
              <li>“Canary + automated gates + instant rollback via flags.”</li>
              <li>“Runbooks are decision trees; postmortems are blameless with tracked actions.”</li>
              <li>“Autoscale on queue depth, lag, RPS/pod, and p95 — not just CPU.”</li>
            </ul>

            <TemplateCard title="One clean closing line (template)">
{`“I design observability so we can detect in minutes, mitigate in one action, and root-cause with metrics→traces→logs.”`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — 20-second observability answer`}>
{`“We publish RED for endpoints and USE for dependencies, alert on error-budget burn, and keep dashboards drillable:
metrics to exemplars to traces and logs. We ship with canaries gated on SLO deltas, run incidents with clear roles,
and scale on meaningful signals like lag and RPS/pod.”`}
            </ExampleCard>
          </Section>
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
